import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import * as ReportingActions from './reporting.actions';
import * as AppRootActions from '@app/app.actions';
import * as ProfileActions from '@profile/profile.actions';

import { ReportEndValues, REPORTING_PERFORMANCE_FILTER, TransactionItem } from '@shared/models/Reporting';
import { SuperhumanQuestion } from '@shared/models/Superhuman';
import { ReportingService } from '@shared/services/reporting.service';

@Injectable()
export class ReportingEffects {
  getTransactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.fetchTransactions),
      switchMap(() =>
        this.reportingService.getTransactions().pipe(
          takeUntil(this.actions$.pipe(ofType(AppRootActions.switchAccount, ProfileActions.logout))),
          map((res: { report_transactions: TransactionItem[] }) => {
            return ReportingActions.fetchTransactionsSuccess({ transactions: res.report_transactions });
          }),
          catchError(() => {
            return EMPTY;
          })
        )
      )
    )
  );

  fetchEndValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.fetchEndValues),
      switchMap(() =>
        this.reportingService.getEndValues().pipe(
          takeUntil(this.actions$.pipe(ofType(AppRootActions.switchAccount, ProfileActions.logout))),
          map((response: { customer: { end_values: ReportEndValues } }) => {
            return ReportingActions.fetchEndValuesSuccess({ endValues: response.customer.end_values });
          }),
          catchError(() => {
            return of(ReportingActions.fetchEndValuesStatus({ value: false }));
          })
        )
      )
    )
  );

  getGraphData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.getGraphData),
      switchMap(() =>
        this.reportingService.getReports().pipe(
          takeUntil(this.actions$.pipe(ofType(AppRootActions.switchAccount, ProfileActions.logout))),
          mergeMap((res) => [
            ReportingActions.getCurrentMonthSuccess({ data: res.customer.reporting.current_month }),
            ReportingActions.getGraphDataSuccess({ data: res.customer.reporting.reporting })
          ]),
          catchError(() => {
            return of(ReportingActions.getGraphDataUnsuccess({ error: true }));
          })
        )
      )
    )
  );

  getCustomerNotifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportingActions.getCustomerNotifications),
      mergeMap(() =>
        this.reportingService.getNotifications().pipe(
          map((response) => {
            return ReportingActions.getCustomerNotificationsSuccess({ data: response.data });
          }),
          catchError(() => EMPTY)
        )
      )
    );
  });

  persistPerformanceFilter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.changePerformanceFilter),
        tap(({ payload }) => {
          if (typeof localStorage === 'undefined') {
            return;
          }
          localStorage.setItem(REPORTING_PERFORMANCE_FILTER, JSON.stringify(payload));
        })
      ),
    { dispatch: false }
  );

  fetchSuperhumanSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.fetchSuperhumanSurvey),
      mergeMap(() =>
        this.reportingService.getSuperHumanSurvey().pipe(
          map((response: { id: number; name: string; survey_questions: SuperhumanQuestion[] }) => {
            return ReportingActions.fetchSuperhumanSurveySuccess({ survey: response.survey_questions });
          }),
          catchError((error) => {
            return of(ReportingActions.fetchSuperhumanSurveyFailed({ error }));
          })
        )
      )
    )
  );

  ignoreSuperhumanSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.ignoreSuperhumanSurvey),
      mergeMap((action) => {
        return this.reportingService.markAsSeenNotification(action.notificationId).pipe(
          map((res) => {
            if (res.success) {
              return ReportingActions.ignoreSuperhumanSurveySuccess();
            }
            return ReportingActions.ignoreSuperhumanSurveyFailed();
          }),
          catchError(() => of(ReportingActions.ignoreSuperhumanSurveyFailed()))
        );
      })
    )
  );

  ignoreSuperhumanSurveySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.ignoreSuperhumanSurveySuccess),
      map(() => ReportingActions.getCustomerNotifications())
    )
  );

  constructor(
    private actions$: Actions,
    private reportingService: ReportingService
  ) {}
}
